import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { fetchExpiryReport } from '../../../api';

const EXPIRY_FILTERS = [
  { key: 'expired', label: 'Expired' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'one_week_to_one_day', label: '1 week - 1 day' },
  { key: 'one_month_to_one_week', label: '1 month - 1 week' },
  { key: 'three_to_one_month', label: '3 months - 1 month' },
  { key: 'six_to_three_months', label: '6 months - 3 months' },
];

const getFilterLabel = (key) => {
  return EXPIRY_FILTERS.find((item) => item.key === key)?.label || 'Expired';
};

const formatMoney = (value) => {
  return `Rs. ${Number(value || 0).toLocaleString('en-LK', {
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-LK');

const formatExcelDateOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-LK');
};

const setColumnWidths = (worksheet, widths) => {
  worksheet['!cols'] = widths.map((width) => ({ wch: width }));
};

const buildExpiryRows = (reportData) => {
  return (reportData?.rows || []).map((item) => ({
    Item: item.itemName || '',
    Batch: item.batchNo || '',
    Strength: item.strength || '',
    Supplier: item.supplier || '',
    'Current Stock': item.currentStock || 0,
    'Expiry Date': formatExcelDateOnly(item.expiryDate),
    'Days Until Expiry':
      item.daysUntilExpiry == null ? '' : item.daysUntilExpiry,
    'Expiry Group': item.expiryStatus || '',
    'Wholesale Price': item.wholesalePrice || 0,
    'Stock Value': item.stockValue || 0,
  }));
};

const downloadExpiryExcel = ({ filename, reportData }) => {
  if (!reportData) return;

  const rows = buildExpiryRows(reportData);

  const headers = [
    'Item',
    'Batch',
    'Strength',
    'Supplier',
    'Current Stock',
    'Expiry Date',
    'Days Until Expiry',
    'Expiry Group',
    'Wholesale Price',
    'Stock Value',
  ];

  const sheetRows = [
    headers,
    ...rows.map((row) => [
      row.Item,
      row.Batch,
      row.Strength,
      row.Supplier,
      row['Current Stock'],
      row['Expiry Date'],
      row['Days Until Expiry'],
      row['Expiry Group'],
      row['Wholesale Price'],
      row['Stock Value'],
    ]),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);

  setColumnWidths(worksheet, [
    34, // Item
    16, // Batch
    14, // Strength
    24, // Supplier
    16, // Current Stock
    16, // Expiry Date
    20, // Days Until Expiry
    22, // Expiry Group
    18, // Wholesale Price
    16, // Stock Value
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Expiry Report');

  XLSX.writeFile(workbook, filename);
};

const ExpiryReportCard = () => {
  const [expiryFilter, setExpiryFilter] = useState('expired');
  const [reportData, setReportData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = async (params = { expiryFilter }) => {
    try {
      setLoading(true);
      setError('');

      const res = await fetchExpiryReport(params);
      setReportData(res.data);
    } catch (err) {
      console.error('Failed to fetch expiry report:', err);
      setError(err?.response?.data?.message || 'Failed to load expiry report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport({ expiryFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (value) => {
    setExpiryFilter(value);
    loadReport({ expiryFilter: value });
  };

  const handleDownload = () => {
    downloadExpiryExcel({
      filename: `expiry-report-${expiryFilter}.xlsx`,
      reportData,
    });
  };

  const summary = reportData?.summary || {};
  const rows = buildExpiryRows(reportData);

  const expiredAndToday =
    Number(summary.expiredItems || 0) + Number(summary.expiresTodayItems || 0);

  return (
    <>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '22px',
          p: { xs: 2, sm: 3 },
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
          background: '#fff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '16px',
              background: '#fff7ed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EventBusyOutlinedIcon
              sx={{
                color: '#ea580c',
                fontSize: 30,
              }}
            />
          </Box>

          <Chip
            label={`${formatNumber(summary.totalRows)} ${
    summary.totalRows === 1 ? 'item' : 'items'
  }`}
            sx={{
              borderRadius: '999px',
              background: '#ffedd5',
              color: '#9a3412',
              fontWeight: 800,
              fontSize: 14,
              height: 34,
              px: 1,
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: 23,
            fontWeight: 900,
            color: '#111827',
            mb: 1,
          }}
        >
          Expiry Report
        </Typography>

        <Typography
          sx={{
            fontSize: 15.5,
            lineHeight: 1.65,
            color: '#6b7280',
            mb: 2.5,
          }}
        >
          Expired and near-expiry stock grouped by the same expiry ranges used in inventory.
        </Typography>

       {/* Expiry filter controls */}
<Box
  sx={{
    mb: 2.5,
    borderRadius: '20px',
    backgroundColor: '#f3f4f6',
    p: 1.4,
  }}
>
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(3, 1fr)',
      },
      gap: 0.8,
    }}
  >
    {EXPIRY_FILTERS.map((item) => {
      const active = expiryFilter === item.key;

      return (
        <Button
          key={item.key}
          onClick={() => handleFilterChange(item.key)}
          disableRipple
          sx={{
            height: 40,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 900,
            fontSize: { xs: '0.78rem', sm: '0.86rem' },
            letterSpacing: '0.01em',
            backgroundColor: active ? '#ffffff' : 'transparent',
            color: active ? '#111827' : '#6b7280',
            boxShadow: active
              ? 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 4px 12px rgba(15, 23, 42, 0.10)'
              : 'none',
            '&:hover': {
              backgroundColor: active
                ? '#ffffff'
                : 'rgba(255,255,255,0.45)',
            },
          }}
        >
          {item.label}
        </Button>
      );
    })}
  </Box>
</Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
            },
            gap: 1.2,
            mb: 2.5,
          }}
        >
          <MiniMetric label="Expired / Today" value={formatNumber(expiredAndToday)} />
          <MiniMetric label="Tomorrow" value={formatNumber(summary.tomorrowItems)} />
          <MiniMetric label="At Risk Value" value={formatMoney(summary.totalAtRiskStockValue)} />
        </Box>

        <Box sx={{ mt: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '150px 1fr',
            },
            gap: 1.4,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              borderColor: '#e5e7eb',
              color: '#374151',
              fontWeight: 800,
              fontSize: 15,
              textTransform: 'none',
              background: '#fff',
              '&:hover': {
                borderColor: '#cbd5e1',
                background: '#f9fafb',
              },
            }}
          >
            Preview
          </Button>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            onClick={handleDownload}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              background: '#0f172a',
              color: '#fff',
              fontWeight: 900,
              fontSize: 15,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                background: '#111827',
                boxShadow: 'none',
              },
              '&:disabled': {
                background: '#94a3b8',
                color: '#fff',
              },
            }}
          >
            Download Excel
          </Button>
        </Box>
        </Box>
      </Card>

      <ExpiryReportPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        expiryFilter={expiryFilter}
        summary={summary}
        rows={rows}
      />
    </>
  );
};

const MiniMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        borderRadius: '14px',
        background: '#f8fafc',
        border: '1px solid #eef2f7',
        p: 1.4,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: '#6b7280',
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 15,
          color: '#111827',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const ExpiryReportPreviewModal = ({
  open,
  onClose,
  expiryFilter,
  summary,
  rows,
}) => {
  const visibleRows = rows || [];

  const expiredAndToday =
    Number(summary.expiredItems || 0) + Number(summary.expiresTodayItems || 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Expiry Report Preview
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: '#6b7280',
              mt: 0.4,
            }}
          >
            Filter: {getFilterLabel(expiryFilter)}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: '#f8fafc',
        }}
      >
        <Box sx={{ p: 2.5, backgroundColor: '#ffffff' }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
              mb: 1.5,
            }}
          >
            Summary
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 1.4,
            }}
          >
            <PreviewMetric label="Expired / Today" value={formatNumber(expiredAndToday)} />
            <PreviewMetric label="Tomorrow" value={formatNumber(summary.tomorrowItems)} />
            <PreviewMetric label="1 Week - 1 Day" value={formatNumber(summary.oneWeekToOneDayItems)} />
            <PreviewMetric label="1 Month - 1 Week" value={formatNumber(summary.oneMonthToOneWeekItems)} />
            <PreviewMetric label="3 Months - 1 Month" value={formatNumber(summary.threeToOneMonthItems)} />
            <PreviewMetric label="6 Months - 3 Months" value={formatNumber(summary.sixToThreeMonthsItems)} />
            <PreviewMetric label="Selected Items" value={formatNumber(summary.totalRows)} />
            <PreviewMetric label="At Risk Value" value={formatMoney(summary.totalAtRiskStockValue)} />
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Expiry Details
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: '#6b7280',
              fontWeight: 600,
              mt: 0.4,
            }}
          >
            These are the same rows included in the downloaded Excel sheet.
          </Typography>
        </Box>

        {visibleRows.length === 0 ? (
          <Box sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography color="text.secondary">
              No stock found for this expiry filter.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              p: 2,
              backgroundColor: '#ffffff',
            }}
          >
            <Box
              sx={{
                maxHeight: '55vh',
                overflow: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '14px',
                backgroundColor: '#ffffff',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1150,
                  '& th': {
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #d1d5db',
                  },
                  '& td': {
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #eef2f7',
                    fontSize: 13,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Strength</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Current Stock</TableCell>
                    <TableCell>Expiry Date</TableCell>
                    <TableCell align="right">Days Until Expiry</TableCell>
                    <TableCell>Expiry Group</TableCell>
                    <TableCell align="right">Wholesale Price</TableCell>
                    <TableCell align="right">Stock Value</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRows.map((row, index) => (
                    <TableRow key={`${row.Item}-${row.Batch}-${index}`}>
                      <TableCell>{row.Item}</TableCell>
                      <TableCell>{row.Batch}</TableCell>
                      <TableCell>{row.Strength}</TableCell>
                      <TableCell>{row.Supplier}</TableCell>
                      <TableCell align="right">
                        {formatNumber(row['Current Stock'])}
                      </TableCell>
                      <TableCell>{row['Expiry Date']}</TableCell>
                      <TableCell align="right">
                        {row['Days Until Expiry']}
                      </TableCell>
                      <TableCell>{row['Expiry Group']}</TableCell>
                      <TableCell align="right">
                        {formatMoney(row['Wholesale Price'])}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(row['Stock Value'])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PreviewMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          color: '#6b7280',
          fontSize: 12,
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: '#111827',
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default ExpiryReportCard;